"use client";
import React, { useState, useEffect } from "react";
import {
  Send,
  ArrowRight,
  CheckCircle,
  Users,
  Building,
  Check,
  X,
  Wallet as WalletIcon,
  Globe,
  Lock,
  Eye,
  Copy,
  Landmark,
  Wallet,
  AlertCircle,
  Info,
  Star,
  Trash2,
  Pencil,
} from "lucide-react";
import { useSelector } from "react-redux";
import { RootState } from "../store";
import {
  collection,
  getDocs,
  addDoc,
  deleteDoc,
  doc,
  getDoc,
  updateDoc,
  query,
  where,
} from "firebase/firestore";
import { db } from "../../firebase";
import { getBalance, getAvailableBalance } from "../services/authapi";
import { ethers } from "ethers";
import toast, { Toaster } from "react-hot-toast";
import { useWallet } from "@crossmint/client-sdk-react-ui";
import { useSearchParams, useNavigate } from "react-router-dom";
import { onSnapshot } from "firebase/firestore";
import { HeadphonesIcon, ShieldAlert } from "lucide-react";
// Polyfill Buffer
import { Buffer } from "buffer";
if (typeof window !== "undefined") {
  window.Buffer = Buffer;
}

interface Recipient {
  id: string;
  name?: string;
  username?: string;
  email?: string | null;
  phone?: string | null;
  wallet: string;
  type: "user" | "vendor";
  photoURL?: string | null;
  isBusiness?: boolean;
}

interface Favorite {
  id: string;
  type: "user" | "vendor" | "wallet";
  name: string;
  wallet: string;
  email?: string | null;
  phone?: string | null;
  isBusiness?: boolean;
}

interface ExternalAccount {
  id: string;
  bank_name: string;
  account_name?: string;
  account_owner_name: string;
  currency: string;
  account: {
    last_4: string;
    routing_number: string;
    checking_or_savings: string;
  };
  active: boolean;
  first_name?: string;
  last_name?: string;
  address?: {
    street_line_1: string;
    street_line_2?: string;
    city: string;
    state: string;
    postal_code: string;
    country: string;
  };
}

interface AddFavorite {
  type: "user" | "vendor" | "wallet";
  wallet: string;
  email?: string | null;
  phone?: string | null;
  isBusiness?: boolean;
}

export function TransferPage() {
  const [amount, setAmount] = useState("");
  const [isMaxSelected, setIsMaxSelected] = useState(false);
  const { wallet } = useWallet();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedRecipient, setSelectedRecipient] = useState<Recipient | null>(
    null,
  );
  const [recipientType, setRecipientType] = useState<
    "cashtap" | "wallet" | "bank" | any
  >("cashtap");
  const [walletBalance, setWalletBalance] = useState<string>("0");
  // `availableBalance` is what a transfer may actually spend; `walletBalance`
  // stays the true total. Bridge deposits are held in custody until admin
  // approval and never appear in the wallet balance, so available == balance.
  const [availableBalance, setAvailableBalance] = useState<string>("0");
  const [loading, setLoading] = useState<boolean>(false);
  const [allCashtapRecipients, setAllCashtapRecipients] = useState<Recipient[]>(
    [],
  );
  const [filteredResults, setFilteredResults] = useState<Recipient[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showBankDetailsModal, setShowBankDetailsModal] = useState(false);
  // Bridge off-ramp states
  const [bridgeLiquidationAddress, setBridgeLiquidationAddress] = useState<
    string | null
  >(null);
  const [hasActiveLiquidationAddress, setHasActiveLiquidationAddress] =
    useState(false);
  const [loadingBankStatus, setLoadingBankStatus] = useState(true);
  const [customerId, setCustomerId] = useState<string | null>(null);
  const [externalAccount, setExternalAccount] =
    useState<ExternalAccount | null>(null);
  const [transferMethod, setTransferMethod] = useState<"ach" | "swift">("ach");
  // Favorites
  const [favorites, setFavorites] = useState<Favorite[]>([]);
  const [showFavoritesModal, setShowFavoritesModal] = useState(false);
  const [favoritesSearch, setFavoritesSearch] = useState("");
  const [showAddFavoriteModal, setShowAddFavoriteModal] = useState(false);
  const [selectedToAdd, setSelectedToAdd] = useState<AddFavorite | null>(null);
  const [customName, setCustomName] = useState("");
  const [showEditFavoriteModal, setShowEditFavoriteModal] = useState(false);
  const [selectedToEdit, setSelectedToEdit] = useState<Favorite | null>(null);
  const [editName, setEditName] = useState("");
  const [showTransferDisabledModal, setShowTransferDisabledModal] = useState(false);
  const user = useSelector((state: RootState) => state.user) as any;
  const senderWallet = user?.smartWallet || "";
  const senderUsername = user?.username || "User";
  const currentUserId = user?.uid || user?.id; // Firebase Auth UID
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isBridgeMode = searchParams.get("bridge") === "true";
  const urlToAddress = searchParams.get("to");

  useEffect(() => {
    if (isBridgeMode && urlToAddress) {
      setRecipientType("wallet");
      setSearchQuery(urlToAddress);
      toast.success("Bridge deposit address loaded — ready to send");
    }
  }, [isBridgeMode, urlToAddress]);

  // Check if transfers are disabled on page load
  useEffect(() => {
    if (!senderWallet) return;
    const senderDocLower = senderWallet.toLowerCase();
    const unsub = onSnapshot(doc(db, "users", senderDocLower), (snap) => {
      if (snap.exists() && snap.data()?.transfersDisabled) {
        setShowTransferDisabledModal(true);
      } else {
        setShowTransferDisabledModal(false);
      }
    });
    return () => unsub();
  }, [senderWallet]);

  // Load bridge off-ramp / bank status
  useEffect(() => {
    const loadBridgeStatus = async () => {
      if (!senderWallet) {
        setLoadingBankStatus(false);
        return;
      }
      setLoadingBankStatus(true);
      try {
        const customerRef = doc(
          db,
          "bridge_customers-main",
          senderWallet.toLowerCase(),
        );
        const snap = await getDoc(customerRef);
        if (!snap.exists()) {
          setHasActiveLiquidationAddress(false);
          setBridgeLiquidationAddress(null);
          setExternalAccount(null);
          setCustomerId(null);
          return;
        }
        const custId = snap.data()?.customerId;
        setCustomerId(custId);
        if (!custId) return;

        const eaRes = await fetch(
          `${import.meta.env.VITE_SERVERURL}/bridge/customers/${custId}/external_accounts`,
          { headers: { Authorization: `Bearer ${import.meta.env.VITE_API_SECRET_TOKEN}` } },
        );
        const eaData = await eaRes.json();
        const accounts = eaData.data || [];
        const activeAccount = accounts.find(
          (acc: ExternalAccount) => acc.active,
        );
        setExternalAccount(activeAccount || null);

        const laRes = await fetch(
          `${import.meta.env.VITE_SERVERURL}/bridge/customers/${custId}/liquidation_addresses`,
          { headers: { Authorization: `Bearer ${import.meta.env.VITE_API_SECRET_TOKEN}` } },
        );
        const laData = await laRes.json();
        const addresses = laData.data || [];
        const activeLA = addresses.find(
          (la: any) =>
            la.state === "active" &&
            la.chain === "base" &&
            la.currency === "usdc",
        );
        if (activeLA) {
          setBridgeLiquidationAddress(activeLA.address);
          setHasActiveLiquidationAddress(true);
        }
      } catch (err) {
        console.error("Failed to load bridge/bank status:", err);
        toast.error("Failed to load bank setup status");
      } finally {
        setLoadingBankStatus(false);
      }
    };
    loadBridgeStatus();
  }, [senderWallet]);

  // Load favorites (without photoURL)
  useEffect(() => {
    if (!currentUserId) return;
    const loadFavorites = async () => {
      try {
        const favoritesCollection = collection(
          db,
          "users",
          currentUserId,
          "favorites",
        );
        const favoritesSnapshot = await getDocs(favoritesCollection);
        const favList: Favorite[] = favoritesSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as Favorite[];
        setFavorites(favList);
      } catch (err) {
        console.error("Error loading favorites:", err);
      }
    };
    loadFavorites();
  }, [currentUserId]);

  const sendEmail = async (
    sender_email: string,
    receiver_email: string,
    sender_name: string,
    receiver_name: string,
    endpoint: string,
    transaction_details?: { [key: string]: string },
  ) => {
    try {
      const response = await fetch(
        `${import.meta.env.VITE_SERVERURL}/${endpoint}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${import.meta.env.VITE_API_SECRET_TOKEN}` },
          credentials: "include",
          body: JSON.stringify({
            sender_email,
            receiver_email,
            sender_name,
            receiver_name,
            transaction_details,
          }),
        },
      );
      if (!response.ok) throw new Error("Email failed");
    } catch (err) {
      console.error(err);
      toast.error("Failed to send confirmation email");
    }
  };

  useEffect(() => {
    const fetchBalances = async () => {
      if (!senderWallet) return;
      setLoading(true);
      try {
        // One call gives both the total and the spendable figure (which are
        // now always equal — Bridge custody deposits never touch the wallet
        // balance). If the endpoint is unavailable we fall back to the plain
        // balance read rather than stranding the user.
        try {
          const info = await getAvailableBalance(senderWallet);
          setWalletBalance(info.balance);
          setAvailableBalance(info.available);
        } catch {
          const usdcBal = await getBalance(senderWallet);
          setWalletBalance(usdcBal);
          setAvailableBalance(usdcBal);
        }
      } catch (err: any) {
        toast.error("Failed to fetch balance");
      } finally {
        setLoading(false);
      }
    };
    fetchBalances();
  }, [senderWallet]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch users with photoURL
        const usersSnap = await getDocs(collection(db, "users"));
        const users = usersSnap.docs.map((userDoc) => {
          const data = userDoc.data();
          return {
            id: userDoc.id,
            username: data.username,
            email: data.email || null,
            phone: data.phone || null,
            wallet: data.wallet,
            type: "user" as const,
            photoURL: data.photoURL || null,
            isBusiness: data.isBusiness || false,
          };
        });

        // Vendors usually don't have profile photos
        const vendorsSnap = await getDocs(collection(db, "vendors"));
        const vendors = vendorsSnap.docs.map((doc) => ({
          id: doc.id,
          name: doc.data().name,
          wallet: doc.data().wallet,
          type: "vendor" as const,
          photoURL: null,
        }));

        setAllCashtapRecipients([...users, ...vendors]);
      } catch (err) {
        console.error("Failed to load recipients:", err);
        toast.error("Failed to load recipients");
      }
    };
    fetchData();
  }, []);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.trim();
    setSearchQuery(value);
    setSelectedRecipient(null);
    if (recipientType !== "cashtap") {
      setShowDropdown(false);
      return;
    }
    const term = value.toLowerCase();
    const filtered = allCashtapRecipients.filter((item) => {
      if (item.type === "user") {
        return (
          item.username?.toLowerCase().includes(term) ||
          item.email?.toLowerCase().includes(term) ||
          item.phone?.toLowerCase().includes(term) ||
          item.wallet?.toLowerCase().includes(term)
        );
      }
      return (
        item.name?.toLowerCase().includes(term) ||
        item.wallet?.toLowerCase().includes(term)
      );
    });
    setFilteredResults(filtered);
    setShowDropdown(value.length >= 2 && filtered.length > 0);
  };

  const handleSelectRecipient = (selected: Recipient) => {
    setSelectedRecipient(selected);
    setSearchQuery(
      selected.type === "user" ? selected.username! : selected.name!,
    );
    setShowDropdown(false);
  };

  const addToFavorites = async () => {
    if (!currentUserId) {
      toast.error("Please sign in to use favorites");
      return;
    }

    let defaultName = "";
    let favType: "user" | "vendor" | "wallet" = "user";
    let walletAddr = "";
    let email: string | null = null;
    let phone: string | null = null;

    if (recipientType === "cashtap" && selectedRecipient) {
      favType = selectedRecipient.type;
      defaultName = selectedRecipient.username || selectedRecipient.name || "";
      walletAddr = selectedRecipient.wallet;
      email = selectedRecipient.email || null;
      phone = selectedRecipient.phone || null;
    } else if (recipientType === "wallet" && ethers.isAddress(searchQuery)) {
      favType = "wallet";
      defaultName = `Wallet ${truncateAddress(searchQuery)}`;
      walletAddr = searchQuery;
    } else {
      toast.error("No valid recipient selected");
      return;
    }

    const exists = favorites.some(
      (f) => f.wallet.toLowerCase() === walletAddr.toLowerCase(),
    );
    if (exists) {
      toast("Already in favorites");
      return;
    }

    setSelectedToAdd({ type: favType, wallet: walletAddr, email, phone, isBusiness: selectedRecipient?.isBusiness || false });
    setCustomName(defaultName);
    setShowAddFavoriteModal(true);
  };

  const confirmAddFavorite = async () => {
    if (!selectedToAdd || !customName.trim()) {
      toast.error("Name required");
      return;
    }

    const favData: Omit<Favorite, "id"> = {
      ...selectedToAdd,
      name: customName.trim(),
    };

    try {
      const favoritesRef = collection(db, "users", currentUserId, "favorites");
      await addDoc(favoritesRef, favData);
      toast.success("Added to favorites!");

      const snap = await getDocs(favoritesRef);
      const updated: Favorite[] = snap.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      })) as Favorite[];
      setFavorites(updated);

      setShowAddFavoriteModal(false);
    } catch (err) {
      console.error(err);
      toast.error("Could not add to favorites");
    }
  };

  const confirmEditFavorite = async () => {
    if (!selectedToEdit || !editName.trim()) {
      toast.error("Name required");
      return;
    }

    try {
      await updateDoc(
        doc(db, "users", currentUserId, "favorites", selectedToEdit.id),
        {
          name: editName.trim(),
        },
      );
      toast.success("Updated favorite!");

      const favoritesRef = collection(db, "users", currentUserId, "favorites");
      const snap = await getDocs(favoritesRef);
      const updated: Favorite[] = snap.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      })) as Favorite[];
      setFavorites(updated);

      setShowEditFavoriteModal(false);
    } catch (err) {
      console.error(err);
      toast.error("Could not update favorite");
    }
  };

  const removeFavorite = async (favId: string) => {
    if (!currentUserId) return;
    try {
      await deleteDoc(doc(db, "users", currentUserId, "favorites", favId));
      toast.success("Removed from favorites");
      setFavorites((prev) => prev.filter((f) => f.id !== favId));
    } catch (err) {
      console.error(err);
      toast.error("Failed to remove favorite");
    }
  };

  // NEW: Fetch current photoURL from users collection when rendering favorites
  const getUserPhotoURL = async (wallet: string): Promise<string | null> => {
    try {
      const q = query(collection(db, "users"), where("wallet", "==", wallet.toLowerCase()));
      const querySnapshot = await getDocs(q);
      if (!querySnapshot.empty) {
        const docSnap = querySnapshot.docs[0];
        return docSnap.data().photoURL || null;
      }
      return null;
    } catch (err) {
      console.error("Error fetching user photoURL:", err);
      return null;
    }
  };

  const selectFromFavorites = (fav: Favorite) => {
    if (fav.type === "wallet") {
      setRecipientType("wallet");
      setSearchQuery(fav.wallet);
      setSelectedRecipient(null);
    } else {
      const match = allCashtapRecipients.find(
        (r) => r.wallet?.toLowerCase() === fav.wallet.toLowerCase(),
      );
      let effectiveRecipient: Recipient;
      if (match) {
        effectiveRecipient = match;
        setSearchQuery(match.username || match.name || fav.wallet);
      } else {
        effectiveRecipient = {
          id: fav.id || "fav-" + fav.wallet.slice(-6),
          wallet: fav.wallet,
          type: fav.type as "user" | "vendor",
          username: fav.type === "user" ? fav.name : undefined,
          name: fav.type === "vendor" ? fav.name : undefined,
          email: fav.email,
          phone: fav.phone,
        };
        const display = fav.name?.includes("@")
          ? fav.email || fav.name
          : fav.name || truncateAddress(fav.wallet);
        setSearchQuery(display);
      }
      setSelectedRecipient(effectiveRecipient);
      setRecipientType("cashtap");
    }
    setTimeout(() => {
      setShowFavoritesModal(false);
    }, 100);
  };

  const confirmTransfer = async () => {
    let recipientAddress = selectedRecipient?.wallet || searchQuery;
    if (recipientType === "bank") {
      if (!hasActiveLiquidationAddress || !bridgeLiquidationAddress) {
        toast.error("No active bank withdrawal address found");
        return;
      }
      recipientAddress = bridgeLiquidationAddress;
    }

    try {
      setLoading(true);
      if (!wallet) throw new Error("Wallet not connected");

      // Check if sender's transfers are disabled by admin
      const senderDocLower = senderWallet.toLowerCase();
      if (senderDocLower) {
        const senderDoc = await getDoc(doc(db, "users", senderDocLower));
        if (senderDoc.exists() && senderDoc.data()?.transfersDisabled) {
          toast.error("Your transfers have been disabled by admin. Please contact support.");
          setLoading(false);
          return;
        }
      }

      // Check if recipient's incoming transfers are disabled
      const recipientLower = recipientAddress?.toLowerCase();
      if (recipientLower && ethers.isAddress(recipientLower)) {
        const recipientDoc = await getDoc(doc(db, "users", recipientLower));
        if (recipientDoc.exists() && recipientDoc.data()?.transfersDisabled) {
          toast.error("This recipient's transfers have been disabled. Cannot send funds.");
          setLoading(false);
          return;
        }
      }

      const parsedAmount = parseFloat(amount);
      if (isNaN(parsedAmount) || parsedAmount <= 0)
        throw new Error("Invalid amount");

      let feeAmount = 0;
      if (["cashtap", "wallet", "bank"].includes(recipientType)) {
        feeAmount = parsedAmount * 0.01;
      }

      const { hash: transferHash } = await wallet.send(
        recipientAddress,
        "usdc",
        parsedAmount.toString(),
      );

      let feeTxHash: string | null = null;
      if (feeAmount > 0) {
        const adminWallet = import.meta.env.VITE_Admin_Wallet;
        if (adminWallet && ethers.isAddress(adminWallet)) {
          try {
            const feeRes = await wallet.send(
              adminWallet,
              "usdc",
              feeAmount.toString(),
            );
            feeTxHash = feeRes?.hash || null;
          } catch (e) {
            console.error(e);
          }
        }
        // Notify admin about fee received
        fetch(`${import.meta.env.VITE_SERVERURL}/notify-admin-fee`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${import.meta.env.VITE_API_SECRET_TOKEN}`,
          },
          body: JSON.stringify({
            username: user?.username || "",
            recipientUsername: selectedRecipient?.username || selectedRecipient?.name || "",
            senderWallet: user?.wallet || user?.smartWallet || "",
            recipientWallet: recipientAddress,
            amount: parsedAmount,
            fee: feeAmount,
            txHash: transferHash,
          }),
        }).catch(() => {});
      }

      toast.success(`Transfer sent! Tx: ${transferHash}`);

      // Record transaction in Firestore for instant admin visibility
      const receiverName =
        recipientType === "bank"
          ? "Bank Account"
          : selectedRecipient?.username ||
            selectedRecipient?.name ||
            truncateAddress(recipientAddress);
      try {
        await fetch(`${import.meta.env.VITE_SERVERURL}/record-transaction`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${import.meta.env.VITE_API_SECRET_TOKEN}`,
          },
          body: JSON.stringify({
            txHash: transferHash,
            senderWallet: senderWallet,
            senderName: senderUsername,
            senderEmail: user?.email || "",
            recipientWallet: recipientAddress,
            recipientName: receiverName,
            recipientEmail: selectedRecipient?.email || "",
            amount: parsedAmount,
            type: "sent",
          }),
        });
      } catch (e) {
        console.error("Failed to record transaction:", e);
      }

      // Record the platform fee as a revenue event (feeds admin analytics).
      if (feeAmount > 0) {
        fetch(`${import.meta.env.VITE_SERVERURL}/record-transfer-fee`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${import.meta.env.VITE_API_SECRET_TOKEN}`,
          },
          body: JSON.stringify({
            feeTxHash,
            mainTxHash: transferHash,
            feeAmount,
            grossAmount: parsedAmount,
            senderWallet,
            senderName: senderUsername,
            senderEmail: user?.email || "",
            recipientWallet: recipientAddress,
            recipientName: receiverName,
            recipientEmail: selectedRecipient?.email || "",
            recipientType,
          }),
        }).catch(() => {});
      }

      if (user?.email) {

        await sendEmail(
          user.email,
          "support@cashtap.cash",
          senderUsername,
          receiverName,
          "complete-transfer",
          {
            AMOUNT: parsedAmount.toFixed(2),
            SENDER: senderUsername,
            TRANSFERED_TO: receiverName,
            TRANSACTION_ID: transferHash,
            DATE: new Date().toISOString().split("T")[0],
          },
        );
      }

      setWalletBalance(await getBalance(senderWallet));
      setAmount("");
      if (!isBridgeMode) {
        setSearchQuery("");
        setSelectedRecipient(null);
      }
      setShowModal(false);
      setShowSuccessModal(true);
      setTimeout(() => setShowSuccessModal(false), 2200);
    } catch (err: any) {
      toast.error(err.message || "Transfer failed");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (recipientType === "bank" && !hasActiveLiquidationAddress) {
      toast.error("Please complete bank setup first");
      navigate("/link-bank");
      return;
    }
    const parsed = parseFloat(amount);
    if (!parsed || parsed < 0.5) {
      toast.error("Amount must be at least 0.5 USD");
      return;
    }
    setShowModal(true);
  };

  function truncateAddress(address: string): string {
    if (!address) return "";
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  }

  const toType = isBridgeMode
    ? "Bridge Off-Ramp Deposit"
    : recipientType === "cashtap"
      ? (selectedRecipient?.type === "vendor" || selectedRecipient?.isBusiness)
        ? "Business (CashTap)"
        : "CashTap User"
      : recipientType === "wallet"
        ? "External Wallet"
        : "Bank Account";

  const recipientName =
    recipientType === "bank" && hasActiveLiquidationAddress
      ? "Your Bank Account"
      : selectedRecipient
        ? selectedRecipient.username ||
          selectedRecipient.name ||
          truncateAddress(selectedRecipient.wallet)
        : truncateAddress(searchQuery);

  const amountNum = parseFloat(amount) || 0;
  const hasFee = ["cashtap", "wallet", "bank"].includes(recipientType);
  const feeNum = hasFee ? amountNum * 0.01 : 0;
  const totalNum = amountNum + feeNum;

  const maskContact = (contact?: string | null): string | null => {
    if (!contact) return null;
    if (contact.includes("@")) {
      const [local, domain] = contact.split("@");
      if (local.length <= 2) return contact;
      const maskedLocal = local.slice(0, 2) + "***";
      return `${maskedLocal}@${domain}`;
    }
    if (/^\+?\d{7,15}$/.test(contact.replace(/\s/g, ""))) {
      const digits = contact.replace(/\D/g, "");
      if (digits.length <= 4) return contact;
      const visible = digits.slice(-4);
      const masked = "***".repeat(digits.length - 4);
      return contact.replace(digits, masked + visible);
    }
    return contact;
  };

  const isFavorited = favorites.some(
    (fav) =>
      fav.wallet.toLowerCase() ===
      (selectedRecipient?.wallet || searchQuery).toLowerCase(),
  );

  const filteredFavorites = favorites.filter((fav) => {
    if (!favoritesSearch.trim()) return true;
    const term = favoritesSearch.toLowerCase();
    return (
      fav.name.toLowerCase().includes(term) ||
      fav.wallet.toLowerCase().includes(term) ||
      (fav.email && fav.email.toLowerCase().includes(term)) ||
      (fav.phone && fav.phone.toLowerCase().includes(term))
    );
  });

  return (
    <div className="max-w-5xl mx-auto space-y-8 pb-12">
      <Toaster position="top-right" />

      {/* Transfers Disabled Modal */}
      {showTransferDisabledModal && (
        <div className="fixed inset-0 z-[9999] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-[#0F1419] border border-gray-800/50 rounded-2xl p-8 max-w-md w-full text-center shadow-2xl">
            <div className="w-16 h-16 mx-auto rounded-full bg-red-500/20 flex items-center justify-center mb-5">
              <ShieldAlert className="w-8 h-8 text-red-400" />
            </div>
            <h2 className="text-xl font-bold text-white mb-3">Transfers Disabled</h2>
            <p className="text-gray-400 text-sm mb-6 leading-relaxed">
              Your transfer functionality has been disabled by an administrator.
              If you believe this is an error, please contact our support team for assistance.
            </p>
            <div className="flex flex-col gap-3">
              <button
                onClick={() => navigate("/support")}
                className="w-full py-3 px-4 bg-gradient-to-r from-[#61CE70] to-[#50B060] text-white font-semibold rounded-xl hover:shadow-lg hover:shadow-emerald-500/20 transition-all text-sm flex items-center justify-center gap-2"
              >
                <HeadphonesIcon className="w-4 h-4" />
                Contact Support
              </button>
              <button
                onClick={() => navigate("/dashboard")}
                className="w-full py-3 px-4 bg-gray-800/50 border border-gray-700/50 text-gray-300 font-medium rounded-xl hover:bg-gray-700/50 transition-all text-sm"
              >
                Go to Dashboard
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="mb-8">
        <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
          Transfer Funds
        </h1>
        <p className="text-gray-400 text-lg mt-2">
          Send USD to CashTap users, external wallets, or your linked bank
          account
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* From Wallet */}
        <div className="bg-gradient-to-br from-[#0F1419] to-[#1A1F2B] border border-gray-700/50 rounded-3xl p-8 backdrop-blur-sm">
          <h3 className="text-xl font-bold text-white mb-4">From Wallet</h3>
          <div className="p-6 bg-gray-800/30 rounded-2xl border border-[#61CE70]/20">
            <div className="flex justify-between items-center">
              <div>
                <p className="font-medium text-[#61CE70]">CashTap Wallet</p>
                <p className="text-gray-400 text-sm">USD</p>
              </div>
              <p className="text-white font-bold text-lg">
                {loading
                  ? "Loading..."
                  : `${parseFloat(walletBalance).toFixed(3)} USD`}
              </p>
            </div>
          </div>
        </div>

        {/* Transfer To */}
        <div className="relative z-[100] bg-gradient-to-br from-[#0F1419] to-[#1A1F2B] border border-gray-700/50 rounded-3xl p-8 backdrop-blur-sm">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-xl font-bold text-white">Transfer To</h3>
            <button
              type="button"
              onClick={() => setShowFavoritesModal(true)}
              className="group relative flex items-center gap-2 px-5 py-2.5 text-yellow-200 font-medium transition-all duration-300"
            >
              <div className="relative">
                <Star
                  size={20}
                  className="text-yellow-400/90 group-hover:text-yellow-300 transition-colors"
                />
                <span className="absolute inset-0 rounded-full border-2 border-yellow-400/0 group-hover:border-yellow-400/40 group-hover:animate-ping-slow pointer-events-none" />
              </div>
              My Favorites
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
            {/* CashTap User button */}
            <button
              type="button"
              onClick={() => {
                if (isBridgeMode) return;
                setRecipientType("cashtap");
                setSearchQuery("");
                setSelectedRecipient(null);
                setShowDropdown(false);
              }}
              disabled={isBridgeMode}
              className={`relative group rounded-2xl border transition-all duration-300 ${
                recipientType === "cashtap"
                  ? "border-[#61CE70]/70 bg-gradient-to-br from-[#1A2F22] to-[#0F1A14] shadow-lg shadow-[#61CE70]/20"
                  : "border-gray-700/50 bg-gray-800/20 hover:border-gray-600/60"
              } ${isBridgeMode ? "opacity-50 cursor-not-allowed" : ""}`}
            >
              <div className="absolute inset-0 bg-gradient-to-br from-[#61CE70]/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
              <div className="relative p-2 md:p-6 flex flex-col items-center text-center">
                <div
                  className={`p-4 rounded-xl mb-3 ${recipientType === "cashtap" ? "bg-[#61CE70]/25" : "bg-gray-700/40"}`}
                >
                  <Users
                    className={`w-4 md:w-7 h-4 md:h-7 ${recipientType === "cashtap" ? "text-[#61CE70]" : "text-gray-400"}`}
                  />
                </div>
                <p
                  className={`font-semibold text-sm md:text-lg mb-1 ${recipientType === "cashtap" ? "text-[#81E68A]" : "text-white"}`}
                >
                  CashTap User
                </p>
                <p className="text-gray-400 text-sm flex justify-center items-center">
                  <Check className="text-green-400 mr-1" /> Instant Transfers
                </p>
              </div>
            </button>

            {/* External Wallet button */}
            <button
              type="button"
              onClick={() => {
                setRecipientType("wallet");
                if (!isBridgeMode) {
                  setSearchQuery("");
                  setSelectedRecipient(null);
                }
                setShowDropdown(false);
              }}
              className={`relative group rounded-2xl border transition-all duration-300 h-full ${
                recipientType === "wallet"
                  ? "border-[#61CE70]/70 bg-gradient-to-br from-[#1A2F22] to-[#0F1A14] shadow-lg shadow-[#61CE70]/20"
                  : "border-gray-700/50 bg-gray-800/20 hover:border-gray-600/60"
              }`}
            >
              <div className="absolute inset-0 bg-gradient-to-br from-[#61CE70]/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
              <div className="relative p-2 md:p-6 flex flex-col items-center text-center h-full">
                <div
                  className={`p-4 rounded-xl mb-3 ${recipientType === "wallet" ? "bg-[#61CE70]/25" : "bg-gray-700/40"}`}
                >
                  <WalletIcon
                    className={`md:w-7 w-4 h-4 md:h-7 ${recipientType === "wallet" ? "text-[#61CE70]" : "text-gray-400"}`}
                  />
                </div>
                <div className="flex items-center gap-1.5 mb-1">
                  <p
                    className={`font-semibold text-sm md:text-lg ${
                      recipientType === "wallet"
                        ? "text-[#81E68A]"
                        : "text-white"
                    }`}
                  >
                    Wallet
                  </p>
                  <div className="relative group/info">
                    <Info
                      className={`w-4 h-4 md:w-5 md:h-5 transition-colors ${
                        recipientType === "wallet"
                          ? "text-[#81E68A]/70 group-hover/info:text-[#81E68A]"
                          : "text-gray-500 group-hover/info:text-gray-300"
                      }`}
                    />
                    <div
                      className={`
                        pointer-events-none absolute bottom-full z-[2000] left-1/2 -translate-x-1/2 mb-2
                        w-72 md:w-80 px-4 py-3
                        bg-gray-900/95 backdrop-blur-sm border border-gray-700 rounded-lg
                        text-xs md:text-sm text-gray-200 shadow-xl
                        opacity-0 group-hover/info:opacity-100
                        transition-opacity duration-200
                        hidden md:block
                      `}
                    >
                      <div className="space-y-2.5 z-[100]">
                        <p className="font-medium text-white">
                          Send to any EVM-compatible wallet
                        </p>
                        <p className="leading-relaxed">
                          This transfers <strong>USDC on the Base chain</strong>
                          . Compatible with MetaMask, Rainbow, Coinbase Wallet,
                          etc.
                        </p>
                        <p className="text-gray-400 leading-relaxed">
                          Make sure the receiving wallet supports the{" "}
                          <strong>Base network</strong>.
                        </p>
                        <p className="text-amber-400/90 text-xs pt-1 font-medium">
                          Transfers are irreversible — double-check the address.
                        </p>
                      </div>
                      <div className="absolute left-1/2 bottom-[-6px] -translate-x-1/2 w-3 h-3 rotate-45 bg-gray-900 border-r border-b border-gray-700 z-[2000]" />
                    </div>
                  </div>
                </div>
                <p className="text-gray-400 text-xs md:text-sm flex justify-center items-center mb-2 md:mb-3">
                  <Check className="text-green-400 mr-1.5 w-4 h-4" />{" "}
                  EVM-Compatible Addresses
                </p>
                <div className="mt-1 flex flex-wrap justify-center gap-1.5 md:gap-2 opacity-70 group-hover:opacity-100 transition-opacity">
                  <img
                    src="/icons/eth-logo.png"
                    alt="Ethereum"
                    className="w-4 h-4 md:w-5 md:h-5"
                  />
                  <img
                    src="/icons/polygon-logo.png"
                    alt="Polygon"
                    className="w-4 h-4 md:w-5 md:h-5"
                  />
                  <img
                    src="/icons/bnb-logo.png"
                    alt="BNB Chain"
                    className="w-4 h-4 md:w-5 md:h-5"
                  />
                  <img
                    src="/icons/arbitrum-logo.png"
                    alt="Arbitrum"
                    className="w-4 h-4 md:w-5 md:h-5"
                  />
                  <img
                    src="/icons/optimism-logo.png"
                    alt="Optimism"
                    className="w-4 h-4 md:w-5 md:h-5"
                  />
                  <span className="text-xs text-gray-500 self-center">
                    + more
                  </span>
                </div>
              </div>
            </button>

            {/* Bank Account */}
            <button
              type="button"
              onClick={() => {
                if (loadingBankStatus) return;
                setRecipientType("bank");
                setSearchQuery("");
                setSelectedRecipient(null);
                setShowDropdown(false);
              }}
              className={`
                group relative
                rounded-2xl
                border
                transition-all duration-300
                flex flex-col
                h-full min-h-[180px]
                ${
                  recipientType === "bank"
                    ? "border-emerald-600/60 bg-gradient-to-br from-emerald-950/50 to-teal-950/40 shadow-lg shadow-emerald-900/25"
                    : loadingBankStatus
                      ? "border-gray-800/50 bg-gray-900/30 opacity-70"
                      : "border-gray-700/50 bg-gray-900/30 hover:border-gray-600 hover:bg-gray-800/40 cursor-pointer"
                }
              `}
            >
              {loadingBankStatus && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/30 z-10">
                  <div className="animate-spin rounded-full h-9 w-9 border-t-2 border-b-2 border-emerald-400"></div>
                </div>
              )}
              <div className="relative flex flex-col items-center justify-center text-center flex-grow p-5 md:p-6">
                <div
                  className={`
                    p-4 rounded-2xl mb-4 transition-all duration-300
                    ${
                      recipientType === "bank"
                        ? "bg-emerald-600/15 group-hover:bg-emerald-600/25"
                        : "bg-gray-800/40 group-hover:bg-gray-800/60"
                    }
                  `}
                >
                  <Landmark
                    className={`
                      w-9 h-9 md:w-10 md:h-10 transition-colors duration-200
                      ${recipientType === "bank" ? "text-emerald-400" : "text-gray-500 group-hover:text-gray-300"}
                    `}
                  />
                </div>
                <p
                  className={`
                    font-semibold text-base md:text-lg mb-2
                    ${recipientType === "bank" ? "text-emerald-200" : "text-gray-200"}
                  `}
                >
                  Bank Account
                </p>
                <div className="text-sm text-center leading-relaxed">
                  {hasActiveLiquidationAddress ? (
                    <span className="inline-flex items-center gap-1.5 text-gray-300">
                      <Check className="w-4 h-4 text-emerald-400" /> ACH &
                      International Transfers
                    </span>
                  ) : (
                    <span className="text-amber-400">
                      Connect bank to enable withdrawals
                    </span>
                  )}
                </div>
                {!hasActiveLiquidationAddress && !loadingBankStatus && (
                  <div className="mt-4 text-xs text-amber-400/80 font-medium opacity-80 group-hover:opacity-100 transition-opacity">
                    Click to connect your bank →
                  </div>
                )}
              </div>
            </button>
          </div>

          <div className="mt-5 text-sm text-gray-400 backdrop-blur-sm">
            <div className="flex items-center gap-2.5">
              <div className="flex h-4 w-4 items-center justify-center rounded-full bg-gray-700/40 text-gray-400">
                <Info />
              </div>
              <p className="font-medium text-xs md:text-xs">
                Fees, if any, will be shown before you confirm.
              </p>
            </div>
          </div>

          {isBridgeMode && (
            <div className="mt-5 p-3 bg-[#1A2A3B]/70 border border-[#61CE70]/30 rounded-xl text-sm text-gray-300 flex items-center gap-2">
              <Lock size={16} className="text-[#61CE70]" />
              <span>Bridge deposit active — wallet address locked</span>
            </div>
          )}

          {/* Recipient input + dropdown */}
          <div className="mt-8 relative">
            <input
              type="text"
              value={searchQuery}
              onChange={handleSearchChange}
              disabled={recipientType === "bank"}
              readOnly={recipientType === "bank"}
              className={`w-full px-5 py-4 bg-gray-800/30 border ${
                recipientType === "wallet"
                  ? "border-[#61CE70]/60"
                  : "border-gray-700/50"
              } rounded-2xl text-white placeholder-gray-400 focus:border-[#61CE70] focus:ring-2 focus:ring-[#61CE70]/30 transition-all font-mono ${
                recipientType === "bank"
                  ? "cursor-not-allowed bg-gray-900/50 text-gray-300"
                  : ""
              }`}
              placeholder={
                recipientType === "cashtap"
                  ? "CashTapTag, email, or business name"
                  : recipientType === "wallet"
                    ? "0x..."
                    : ""
              }
              required
            />

            {/* Dropdown with profile images */}
            {showDropdown &&
              recipientType === "cashtap" &&
              filteredResults.length > 0 && (
                <div className="absolute w-full z-[10000] top-full mt-2 bg-gradient-to-br from-[#1A2A3B] to-[#0D1419] border border-gray-700/50 rounded-3xl shadow-[0_0_20px_rgba(97,206,112,0.15)] max-h-72 overflow-y-auto">
                  {filteredResults.map((item) => (
                    <div
                      key={item.id}
                      onClick={() => handleSelectRecipient(item)}
                      className="p-4 hover:bg-[#61CE70]/5 cursor-pointer flex items-center justify-between border-b border-gray-700/50 last:border-b-0"
                    >
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0 border border-gray-600/50">
                          {item.photoURL ? (
                            <img
                              src={item.photoURL}
                              alt={item.username || item.name || "User"}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                (e.target as HTMLImageElement).style.display =
                                  "none";
                                (
                                  e.target as HTMLImageElement
                                ).parentElement!.innerHTML = `
                                  <div class="w-full h-full bg-gray-700 flex items-center justify-center">
                                    <Users class="w-5 h-5 text-gray-400" />
                                  </div>
                                `;
                              }}
                            />
                          ) : (item.type === "user" && !item.isBusiness) ? (
                            <div className="w-full h-full bg-[#61CE70]/20 flex items-center justify-center">
                              <Users className="w-5 h-5 text-[#61CE70]" />
                            </div>
                          ) : (
                            <div className="w-full h-full bg-blue-500/20 flex items-center justify-center">
                              <Building className="w-5 h-5 text-blue-400" />
                            </div>
                          )}
                        </div>
                        <div>
                          <span className="text-white font-medium">
                            {item.type === "user" ? item.username : item.name}
                          </span>
                          {(item.type === "vendor" || item.isBusiness) && (
                            <span className="ml-2 px-2 py-0.5 text-xs font-medium bg-blue-500/20 text-blue-400 rounded-full inline-flex items-center gap-1">
                              <Building className="w-3 h-3" />
                              Business
                            </span>
                          )}
                          <p className="text-gray-400 text-xs mt-0.5">
                            {item.type === "user"
                              ? maskContact(item.email) || "No contact"
                              : truncateAddress(item.wallet)}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        <span className="text-gray-500 text-sm font-mono">
                          {truncateAddress(item.wallet)}
                        </span>
                        {selectedRecipient?.id === item.id && (
                          <Check className="w-5 h-5 text-[#61CE70]" />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

            {/* Add to Favorites */}
            {(selectedRecipient ||
              (recipientType === "wallet" && ethers.isAddress(searchQuery))) &&
              !isFavorited && (
                <button
                  type="button"
                  onClick={addToFavorites}
                  className="group mt-4 flex items-center gap-2 text-yellow-400 hover:text-yellow-300 font-medium text-sm transition-colors duration-300 relative"
                >
                  <Star
                    size={18}
                    className="text-yellow-500/90 group-hover:text-yellow-400 transition-colors"
                  />
                  Add to Favorites
                  <span className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-yellow-400/60 to-transparent scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-center" />
                </button>
              )}
          </div>

          {(selectedRecipient ||
            (recipientType === "wallet" && ethers.isAddress(searchQuery)) ||
            (recipientType === "bank" && hasActiveLiquidationAddress)) && (
            <div className="mt-4 p-4 bg-gray-800/40 rounded-xl border border-[#61CE70]/20 flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-[#61CE70] flex-shrink-0" />
              <div>
                <p className="text-gray-300 text-sm">
                  Selected:{" "}
                  <span className="font-mono text-white font-medium">
                    {recipientType === "bank"
                      ? `${externalAccount?.bank_name || "Bank"} •••• ${externalAccount?.account.last_4 || "••••"}`
                      : truncateAddress(
                          recipientType === "bank"
                            ? bridgeLiquidationAddress || ""
                            : searchQuery,
                        )}
                  </span>
                </p>
                <p className="text-gray-500 text-xs mt-0.5">{toType}</p>
              </div>
            </div>
          )}
        </div>

        {/* Transfer Method */}
        {recipientType === "bank" && (
          <div className="bg-gradient-to-br from-[#0F1419] to-[#1A1F2B] border border-gray-700/50 rounded-3xl p-8 backdrop-blur-sm">
            <h3 className="text-xl font-bold text-white mb-6">
              Withdrawal Method
            </h3>
            {!hasActiveLiquidationAddress && (
              <div className="mb-6 p-4 bg-amber-950/30 border border-amber-800/40 rounded-xl text-sm text-amber-200">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium">
                      Bank setup required for ACH transfers
                    </p>
                    <p className="mt-1">
                      Please complete your bank connection to enable ACH
                      withdrawals.
                    </p>
                    <button
                      type="button"
                      onClick={() => navigate("/link-bank")}
                      className="mt-3 text-amber-300 hover:text-amber-200 underline text-sm font-medium"
                    >
                      Connect Bank Account →
                    </button>
                  </div>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <button
                type="button"
                onClick={() => {
                  if (hasActiveLiquidationAddress) {
                    setTransferMethod("ach");
                  } else {
                    navigate("/link-bank");
                  }
                }}
                disabled={!hasActiveLiquidationAddress}
                className={`relative p-6 rounded-2xl border transition-all duration-300 ${
                  transferMethod === "ach" && hasActiveLiquidationAddress
                    ? "border-[#61CE70] bg-[#61CE70]/10 shadow-lg shadow-[#61CE70]/20"
                    : hasActiveLiquidationAddress
                      ? "border-gray-700/50 bg-gray-800/30 hover:border-gray-600/60"
                      : "border-gray-700/30 bg-gray-900/20 opacity-60 cursor-not-allowed"
                }`}
              >
                <div className="flex flex-col items-center text-center">
                  <div
                    className={`p-4 rounded-xl mb-3 ${
                      hasActiveLiquidationAddress
                        ? "bg-[#61CE70]/20"
                        : "bg-gray-700/30"
                    }`}
                  >
                    <Landmark
                      className={`w-7 h-7 ${hasActiveLiquidationAddress ? "text-[#61CE70]" : "text-gray-500"}`}
                    />
                  </div>
                  <p
                    className={`font-semibold text-lg ${
                      hasActiveLiquidationAddress
                        ? "text-[#81E68A]"
                        : "text-gray-400"
                    }`}
                  >
                    ACH Transfer
                  </p>
                  <p className="text-sm text-gray-400 mt-1">
                    Recommended • 1–3 days
                  </p>
                </div>
                {transferMethod === "ach" && hasActiveLiquidationAddress && (
                  <div className="absolute top-3 right-3">
                    <CheckCircle className="w-6 h-6 text-[#61CE70]" />
                  </div>
                )}
                {!hasActiveLiquidationAddress && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/20 rounded-2xl">
                    <span className="bg-gray-800/80 text-gray-300 px-4 py-1.5 rounded-full text-sm font-medium">
                      Setup Required
                    </span>
                  </div>
                )}
              </button>

              <div className="relative p-6 rounded-2xl border border-gray-700/40 bg-gray-900/30 opacity-70 cursor-not-allowed">
                <div className="flex flex-col items-center text-center">
                  <div className="p-4 bg-gray-700/40 rounded-xl mb-3">
                    <Globe className="w-7 h-7 text-gray-400" />
                  </div>
                  <p className="font-semibold text-lg text-gray-300">
                    SWIFT (International)
                  </p>
                  <p className="text-sm text-gray-500 mt-1">Coming soon</p>
                </div>
                <div className="absolute inset-0 flex items-center justify-center bg-black/30 rounded-2xl">
                  <span className="bg-gray-800/80 text-gray-300 px-4 py-1.5 rounded-full text-sm font-medium">
                    Coming Soon
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Amount */}
        <div className="bg-gradient-to-br from-[#0F1419] to-[#1A1F2B] border border-gray-700/50 rounded-3xl p-8 backdrop-blur-sm">
          <h3 className="text-xl font-bold text-white mb-4">Amount (USD)</h3>

          <div className="relative">
            <input
              type="number"
              value={amount}
              onChange={(e) => {
                setAmount(e.target.value);
                setIsMaxSelected(false);
              }}
              className="w-full px-5 py-4 z-[1000] bg-gray-800/30 border border-gray-700/50 rounded-2xl text-white text-lg placeholder-gray-400 focus:border-[#61CE70] focus:ring-2 focus:ring-[#61CE70]/20 transition-all appearance-none"
              placeholder="0.00"
              min="0.5"
              max={parseFloat(availableBalance)}
              step="0.01"
              required
            />
            <span className="absolute right-5 top-1/2 transform -translate-y-1/2 text-gray-400 text-lg font-medium">
              USD
            </span>
          </div>
          <div className="flex justify-between mt-3 text-sm">
            <p className="text-gray-400">
              Available: {parseFloat(availableBalance).toFixed(3)} USD
            </p>
            <button
              type="button"
              onClick={() => {
                const bal = parseFloat(availableBalance);
                const keep = 0.02;
                const min = 0.5;
                let avail = bal - keep;
                if (hasFee) avail /= 1.01;
                if (avail < min) {
                  toast.error(`Not enough balance for a ${min} USD transfer`);
                  return;
                }
                setAmount(avail.toFixed(2));
                setIsMaxSelected(true);
              }}
              className="text-[#61CE70] hover:text-[#81E68A] transition-colors"
            >
              Max
            </button>
          </div>
          {isMaxSelected && (
            <p className="text-yellow-400 text-sm mt-2">
              {hasFee ? "Includes 1% service fee" : "No fees applied"}
            </p>
          )}
        </div>

        {/* Transfer Summary */}
        <div className="bg-gradient-to-br from-[#0F1419] to-[#1A1F2B] border border-gray-700/50 rounded-3xl p-4 sm:p-8 backdrop-blur-sm">
          <h3 className="text-lg sm:text-xl font-bold text-white mb-4 sm:mb-6">
            Transfer Summary
          </h3>
          <div className="space-y-4 sm:space-y-6">
            <div className="flex items-center justify-between bg-gray-900/40 p-3 sm:p-5 rounded-2xl border border-gray-800">
              <div className="flex-1 text-center min-w-0">
                <div className="inline-flex items-center justify-center w-10 h-10 sm:w-14 sm:h-14 bg-gray-800 rounded-full mb-2">
                  <WalletIcon className="w-5 h-5 sm:w-7 sm:h-7 text-[#61CE70]" />
                </div>
                <p className="text-white font-medium text-sm sm:text-base">Your Wallet</p>
                <p className="text-gray-500 text-[10px] sm:text-xs truncate">
                  {truncateAddress(senderWallet)}
                </p>
              </div>
              <div className="flex flex-col items-center px-2 sm:px-4 shrink-0">
                <ArrowRight className="w-6 h-6 sm:w-8 sm:h-8 text-gray-500" />
                <span className="text-xs text-gray-500 mt-1">→</span>
              </div>
              <div className="flex-1 text-center min-w-0">
                <div className="inline-flex items-center justify-center w-10 h-10 sm:w-14 sm:h-14 rounded-full mb-2 bg-gradient-to-br from-[#61CE70]/20 to-[#50B060]/10">
                  {recipientType === "cashtap" ? (
                    (selectedRecipient?.type === "vendor" || selectedRecipient?.isBusiness) ? (
                      <Building className="w-5 h-5 sm:w-7 sm:h-7 text-blue-400" />
                    ) : (
                      <Users className="w-5 h-5 sm:w-7 sm:h-7 text-[#81E68A]" />
                    )
                  ) : recipientType === "wallet" ? (
                    <WalletIcon className="w-5 h-5 sm:w-7 sm:h-7 text-[#81E68A]" />
                  ) : (
                    <Landmark className="w-5 h-5 sm:w-7 sm:h-7 text-[#81E68A]" />
                  )}
                </div>
                <p className="text-[#81E68A] font-bold text-sm sm:text-base truncate">{recipientName}</p>
                <p className="text-gray-400 text-[10px] sm:text-xs">{toType}</p>
              </div>
            </div>

            <div className="bg-gray-900/30 rounded-2xl p-4 sm:p-6 border border-gray-800 space-y-3 sm:space-y-4">
              <div className="flex justify-between text-sm sm:text-base">
                <span className="text-gray-300">Amount to send</span>
                <span className="text-white font-medium">
                  {amountNum.toFixed(2)} USD
                </span>
              </div>
              {hasFee && (
                <div className="flex justify-between text-sm sm:text-base">
                  <span className="text-gray-300">Service Fee (1%)</span>
                  <span className="text-white font-medium">
                    {feeNum.toFixed(2)} USD
                  </span>
                </div>
              )}
              <div className="border-t border-gray-700 pt-3 sm:pt-4 flex justify-between items-center">
                <span className="text-base sm:text-lg font-medium text-white">
                  Total deducted
                </span>
                <span className="text-xl sm:text-2xl font-bold text-[#81E68A]">
                  {totalNum.toFixed(2)} USD
                </span>
              </div>
            </div>
          </div>
        </div>

        <button
          type="submit"
          disabled={
            !amount ||
            (recipientType !== "bank" && !searchQuery) ||
            totalNum > parseFloat(availableBalance) ||
            loading ||
            (recipientType === "bank" && !hasActiveLiquidationAddress)
          }
          className="w-full bg-gradient-to-r from-[#61CE70] to-[#50B060] text-white py-5 rounded-2xl font-medium hover:shadow-xl hover:shadow-[#61CE70]/25 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3 text-lg"
        >
          <Send className="w-6 h-6" />
          <span>
            {loading
              ? "Processing..."
              : recipientType === "bank"
                ? "Withdraw to Bank"
                : "Send Now"}
          </span>
        </button>
      </form>

      {/* Favorites Modal */}
      {showFavoritesModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[2000] p-4">
          <div className="bg-gradient-to-br from-[#0F1419] to-[#1A1F2B] border border-gray-700 rounded-3xl w-full max-w-lg max-h-[85vh] flex flex-col">
            {/* Header */}
            <div className="sticky top-0 bg-[#0F1419] rounded-3xl border-b border-gray-700 p-6 flex items-center justify-between z-10">
              <h3 className="text-2xl font-bold text-white flex items-center gap-3">
                <Star className="text-[#61CE70]" size={28} />
                My Favorites
              </h3>
              <button
                onClick={() => setShowFavoritesModal(false)}
                className="p-2 hover:bg-gray-800 rounded-full transition-colors"
              >
                <X size={28} className="text-gray-400 hover:text-white" />
              </button>
            </div>

            {/* Search input */}
            <div className="p-6 border-b border-gray-700">
              <div className="relative">
                <input
                  type="text"
                  value={favoritesSearch}
                  onChange={(e) => setFavoritesSearch(e.target.value)}
                  placeholder="Search by name, wallet, email..."
                  className="w-full pl-10 pr-4 py-3 bg-gray-800/40 border border-gray-700/70 rounded-xl text-white placeholder-gray-500 focus:border-[#61CE70] focus:ring-1 focus:ring-[#61CE70]/40 outline-none transition-all"
                />
                <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none">
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                </div>
              </div>
            </div>

            {/* Content - scrollable area */}
            <div className="flex-1 overflow-y-auto px-6 py-6 scrollbar-hide">
              {favorites.length === 0 ? (
                <div className="text-center py-12 text-gray-400">
                  <Star className="w-12 h-12 mx-auto mb-4 opacity-40" />
                  <p className="text-lg">No favorites yet</p>
                  <p className="mt-2">
                    Add users or wallets to favorites for quick transfers
                  </p>
                </div>
              ) : filteredFavorites.length === 0 ? (
                <div className="text-center py-12 text-gray-400">
                  <p className="text-lg">No matching favorites</p>
                  <p className="mt-2">Try a different search term</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {filteredFavorites.map((fav) => {
                    // Try to find current recipient data (which has up-to-date photoURL)
                    const match = allCashtapRecipients.find(
                      (r) =>
                        r.wallet?.toLowerCase() === fav.wallet?.toLowerCase(),
                    );

                    // Determine what to show in the avatar circle
                    let avatarContent;

                    if (match?.photoURL && fav.type === "user") {
                      // Show real profile picture if available
                      avatarContent = (
                        <img
                          src={match.photoURL}
                          alt={fav.name || "User"}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.currentTarget.style.display = "none";
                            e.currentTarget.parentElement!.innerHTML = `
                  <div class="w-full h-full bg-gray-700 flex items-center justify-center">
                    <Users class="w-6 h-6 text-gray-400" />
                  </div>
                `;
                          }}
                        />
                      );
                    } else {
                      // Fallback icon based on type
                      avatarContent = (
                        <div className={`w-full h-full flex items-center justify-center ${(fav.type === "vendor" || fav.isBusiness) ? "bg-blue-500/20" : "bg-[#61CE70]/20"}`}>
                          {(fav.type === "vendor" || fav.isBusiness) ? (
                            <Building className="w-6 h-6 text-blue-400" />
                          ) : fav.type === "user" ? (
                            <Users className="w-6 h-6 text-[#61CE70]" />
                          ) : (
                            <WalletIcon className="w-6 h-6 text-[#61CE70]" />
                          )}
                        </div>
                      );
                    }

                    return (
                      <div
                        key={fav.id}
                        className="flex items-center justify-between p-4 bg-gray-900/60 rounded-2xl border border-gray-800 hover:border-gray-700 transition-all cursor-pointer group"
                        onClick={() => selectFromFavorites(fav)}
                      >
                        <div className="flex items-center gap-4 flex-1 min-w-0">
                          {/* Avatar */}
                          <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0 border border-gray-600/50">
                            {avatarContent}
                          </div>

                          {/* Info */}
                          <div className="min-w-0">
                            <p className="font-medium text-white truncate">
                              {fav.name || truncateAddress(fav.wallet)}
                              {(fav.type === "vendor" || fav.isBusiness) && (
                                <span className="ml-2 px-2 py-0.5 text-xs font-medium bg-blue-500/20 text-blue-400 rounded-full inline-flex items-center gap-1 align-middle">
                                  <Building className="w-3 h-3" />
                                  Business
                                </span>
                              )}
                            </p>
                            <p className="text-sm text-gray-400 truncate">
                              {fav.type === "wallet"
                                ? truncateAddress(fav.wallet)
                                : maskContact(fav.email) ||
                                  fav.phone ||
                                  "No contact"}
                            </p>
                          </div>
                        </div>

                        {/* Actions (visible on hover) */}
                        <div className="flex items-center space-x-3 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedToEdit(fav);
                              setEditName(fav.name);
                              setShowEditFavoriteModal(true);
                            }}
                            className="p-2 text-gray-500 hover:text-[#61CE70] transition-colors"
                          >
                            <Pencil size={20} />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              removeFavorite(fav.id);
                            }}
                            className="p-2 text-gray-500 hover:text-red-400 transition-colors"
                          >
                            <Trash2 size={20} />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Add Favorite Modal */}
      {showAddFavoriteModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[2000] p-4">
          <div className="bg-gradient-to-br from-[#0F1419] to-[#1A1F2B] border border-gray-700 rounded-3xl w-full max-w-md p-6">
            <h3 className="text-2xl font-bold text-white mb-6">
              Add to Favorites
            </h3>
            <input
              type="text"
              value={customName}
              onChange={(e) => setCustomName(e.target.value)}
              className="w-full px-4 py-3 bg-gray-800/30 border border-gray-700/50 rounded-2xl text-white placeholder-gray-400 focus:border-[#61CE70] transition-all"
              placeholder="Enter Nick name"
            />
            <div className="flex gap-4 mt-6">
              <button
                onClick={() => setShowAddFavoriteModal(false)}
                className="flex-1 py-3 bg-gray-800 hover:bg-gray-700 rounded-2xl transition-colors text-white"
              >
                Cancel
              </button>
              <button
                onClick={confirmAddFavorite}
                className="flex-1 py-3 bg-gradient-to-r from-[#61CE70] to-[#50B060] text-white rounded-2xl hover:shadow-lg hover:shadow-[#61CE70]/30 transition-all"
              >
                Add
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Favorite Modal */}
      {showEditFavoriteModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[2000] p-4">
          <div className="bg-gradient-to-br from-[#0F1419] to-[#1A1F2B] border border-gray-700 rounded-3xl w-full max-w-md p-6">
            <h3 className="text-2xl font-bold text-white mb-6">
              Edit Favorite
            </h3>
            <input
              type="text"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              className="w-full px-4 py-3 bg-gray-800/30 border border-gray-700/50 rounded-2xl text-white placeholder-gray-400 focus:border-[#61CE70] transition-all"
              placeholder="Enter new name"
            />
            <div className="flex gap-4 mt-6">
              <button
                onClick={() => setShowEditFavoriteModal(false)}
                className="flex-1 py-3 bg-gray-800 hover:bg-gray-700 rounded-2xl transition-colors text-white"
              >
                Cancel
              </button>
              <button
                onClick={confirmEditFavorite}
                className="flex-1 py-3 bg-gradient-to-r from-[#61CE70] to-[#50B060] text-white rounded-2xl hover:shadow-lg hover:shadow-[#61CE70]/30 transition-all"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bank Details Modal */}
      {showBankDetailsModal && externalAccount && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[2000] p-4">
          <div className="bg-gradient-to-br from-[#0F1419] to-[#1A1F2B] border border-gray-700 rounded-3xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-[#0F1419] border-b border-gray-700 p-6 flex items-center justify-between z-10">
              <h3 className="text-2xl font-bold text-white flex items-center gap-3">
                <Landmark className="text-[#61CE70]" size={28} />
                Linked Bank Account
              </h3>
              <button
                onClick={() => setShowBankDetailsModal(false)}
                className="p-2 hover:bg-gray-800 rounded-full transition-colors"
              >
                <X size={28} className="text-gray-400 hover:text-white" />
              </button>
            </div>

            <div className="p-6 space-y-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-5">
                  <div>
                    <p className="text-gray-400 text-sm mb-1">Bank Name</p>
                    <p className="text-white font-medium text-lg">
                      {externalAccount.bank_name || "—"}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm mb-1">Account Holder</p>
                    <p className="text-white font-medium">
                      {externalAccount.account_owner_name || "—"}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm mb-1">Account Type</p>
                    <p className="text-white font-medium capitalize">
                      {externalAccount.account.checking_or_savings || "—"}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm mb-1">Last 4 Digits</p>
                    <div className="flex items-center gap-3">
                      <p className="text-white font-mono text-lg">
                        •••• {externalAccount.account.last_4 || "—"}
                      </p>
                      <button
                        onClick={() =>
                          navigator.clipboard.writeText(
                            externalAccount.account.last_4,
                          )
                        }
                        className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
                      >
                        <Copy
                          size={18}
                          className="text-gray-400 hover:text-white"
                        />
                      </button>
                    </div>
                  </div>
                </div>

                <div className="space-y-5">
                  <div>
                    <p className="text-gray-400 text-sm mb-1">Currency</p>
                    <p className="text-white font-medium">
                      {externalAccount.currency?.toUpperCase() || "USD"}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm mb-1">Status</p>
                    <p className="text-[#61CE70] font-medium">
                      {externalAccount.active ? "Active" : "Inactive"}
                    </p>
                  </div>
                  {(externalAccount.first_name ||
                    externalAccount.last_name) && (
                    <div>
                      <p className="text-gray-400 text-sm mb-1">
                        Registered Name
                      </p>
                      <p className="text-white font-medium">
                        {(externalAccount.first_name || "") +
                          " " +
                          (externalAccount.last_name || "")}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {externalAccount.address && (
                <div className="pt-6 border-t border-gray-700">
                  <h4 className="text-lg font-semibold text-white mb-4">
                    Registered Address
                  </h4>
                  <div className="space-y-2 text-gray-300">
                    <p>{externalAccount.address.street_line_1}</p>
                    {externalAccount.address.street_line_2 && (
                      <p>{externalAccount.address.street_line_2}</p>
                    )}
                    <p>
                      {externalAccount.address.city},{" "}
                      {externalAccount.address.state}{" "}
                      {externalAccount.address.postal_code}
                    </p>
                    <p>{externalAccount.address.country}</p>
                  </div>
                </div>
              )}

              <div className="pt-6 text-sm text-gray-500 italic border-t border-gray-700">
                This account is used for withdrawals. Funds typically arrive in
                1–3 business days via ACH.
              </div>
            </div>

            <div className="p-6 border-t border-gray-700 bg-[#0F1419]">
              <button
                onClick={() => setShowBankDetailsModal(false)}
                className="w-full py-4 bg-gray-800 hover:bg-gray-700 text-white font-medium rounded-2xl transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/70 flex items-end sm:items-center justify-center z-[1000] sm:p-4">
          <div className="bg-gradient-to-br from-[#0F1419] to-[#1A1F2B] border border-[#61CE70]/30 rounded-t-3xl sm:rounded-3xl p-4 sm:p-8 sm:max-w-lg w-full h-[95vh] sm:h-auto overflow-y-auto relative shadow-2xl shadow-[#61CE70]/10">
            <button
              onClick={() => setShowModal(false)}
              className="absolute top-3 right-3 sm:top-4 sm:right-4 text-gray-400 hover:text-white"
            >
              <X size={22} />
            </button>
            <h2 className="text-xl sm:text-2xl font-bold text-white mb-4 sm:mb-6 flex items-center gap-2 sm:gap-3">
              <CheckCircle className="w-6 h-6 sm:w-7 sm:h-7 text-[#61CE70] shrink-0" />
              Confirm Transfer
            </h2>
            <div className="space-y-4 sm:space-y-6 mb-6 sm:mb-8">
              <div className="flex justify-between items-center gap-2">
                <span className="text-gray-400 text-sm sm:text-base shrink-0">From</span>
                <span className="text-white font-medium text-sm sm:text-base text-right truncate">
                  CashTap Wallet ({senderUsername})
                </span>
              </div>
              <div>
                <span className="text-gray-400 block mb-2 text-sm sm:text-base">To</span>
                {recipientType === "bank" && externalAccount ? (
                  <div className="bg-gray-900/60 border border-gray-700/60 rounded-2xl p-3 sm:p-5">
                    <div className="flex items-center gap-3 sm:gap-4">
                      <div className="p-2.5 sm:p-3.5 bg-[#61CE70]/15 rounded-xl shrink-0">
                        <Landmark className="w-5 h-5 sm:w-7 sm:h-7 text-[#61CE70]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-white text-base sm:text-lg truncate">
                          {externalAccount.bank_name}
                        </p>
                        <p className="text-gray-400 text-xs sm:text-sm mt-0.5">
                          •••• {externalAccount.account.last_4} •{" "}
                          {externalAccount.account.checking_or_savings}
                        </p>
                        <p className="text-gray-500 text-xs mt-1 truncate">
                          Owner: {externalAccount.account_owner_name || "—"}
                        </p>
                      </div>
                    </div>
                  </div>
                ) : recipientType === "cashtap" && selectedRecipient ? (
                  <div className="bg-gray-900/60 border border-gray-700/60 rounded-2xl p-3 sm:p-5">
                    <div className="flex items-center gap-3 sm:gap-4">
                      <div className="p-2.5 sm:p-3.5 bg-[#61CE70]/15 rounded-xl shrink-0">
                        {selectedRecipient.type === "vendor" ? (
                          <Building className="w-5 h-5 sm:w-7 sm:h-7 text-[#61CE70]" />
                        ) : (
                          <Users className="w-5 h-5 sm:w-7 sm:h-7 text-[#61CE70]" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
                          <p className="font-semibold text-white text-base sm:text-lg truncate">
                            {selectedRecipient.username ||
                              selectedRecipient.name}
                          </p>
                          {selectedRecipient.type === "vendor" && (
                            <span className="px-2 py-0.5 sm:px-2.5 sm:py-1 text-[10px] sm:text-xs font-bold bg-gradient-to-r from-[#61CE70]/20 to-[#50B060]/20 text-[#81E68A] rounded-full border border-[#61CE70]/30">
                              Business
                            </span>
                          )}
                        </div>
                        <p className="text-gray-400 text-xs sm:text-sm mt-0.5 truncate">
                          {selectedRecipient.type === "user"
                            ? maskContact(
                                selectedRecipient.email ||
                                  selectedRecipient.phone,
                              ) || "CashTap User"
                            : "CashTap Business"}
                        </p>
                        <p className="text-gray-500 text-xs mt-1 font-mono truncate">
                          {truncateAddress(selectedRecipient.wallet)}
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="bg-gray-900/60 border border-gray-700/60 rounded-2xl p-3 sm:p-5">
                    <div className="flex items-center gap-3 sm:gap-4">
                      <div className="p-2.5 sm:p-3.5 bg-[#61CE70]/15 rounded-xl shrink-0">
                        <Wallet className="w-5 h-5 sm:w-7 sm:h-7 text-[#61CE70]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-white text-base sm:text-lg break-all">
                          {recipientName}
                        </p>
                        <p className="text-gray-400 text-xs sm:text-sm mt-0.5">
                          External Wallet
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div className="flex justify-between text-sm sm:text-base">
                <span className="text-gray-400">Amount</span>
                <span className="text-white">{amountNum.toFixed(2)} USD</span>
              </div>
              <div className="flex justify-between text-sm sm:text-base">
                <span className="text-gray-400">
                  Fee {hasFee ? "(1%)" : ""}
                </span>
                <span className="text-white">{feeNum.toFixed(2)} USD</span>
              </div>
              <div className="border-t border-gray-700 pt-3 sm:pt-4 flex justify-between items-center">
                <span className="text-white font-medium text-base sm:text-lg">Total</span>
                <span className="text-[#61CE70] font-bold text-xl sm:text-2xl">
                  {totalNum.toFixed(2)} USD
                </span>
              </div>
            </div>

            <div className="my-4 sm:my-5 p-3 sm:p-4 bg-amber-950/40 border border-amber-600/50 rounded-lg text-xs sm:text-sm text-amber-100/90">
              <div className="flex items-start gap-2 sm:gap-3">
                <AlertCircle className="w-4 h-4 sm:w-5 sm:h-5 text-amber-400 mt-0.5 flex-shrink-0" />
                <div className="space-y-1 sm:space-y-1.5">
                  <p className="font-medium">
                    By continuing, you confirm this transfer is authorized by
                    you and is final
                    <strong className="text-amber-300 ml-1">
                      Transfers are irreversible.
                    </strong>
                    .
                  </p>
                  <p className="text-amber-200/80">
                    CashTap does not hold, cancel, or reverse completed
                    transfers. Beware of scams.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex gap-3 sm:gap-4">
              <button
                onClick={() => setShowModal(false)}
                className="flex-1 py-3 sm:py-3.5 bg-gray-800 hover:bg-gray-700 rounded-2xl transition-colors text-sm sm:text-base"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowModal(false);
                  confirmTransfer();
                }}
                disabled={loading}
                className="flex-1 py-3 sm:py-3.5 bg-gradient-to-r from-[#61CE70] to-[#50B060] text-white rounded-2xl hover:shadow-lg hover:shadow-[#61CE70]/30 transition-all disabled:opacity-50 text-sm sm:text-base"
              >
                {loading ? "Processing..." : "Confirm & Send"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showSuccessModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[1000]">
          <div className="bg-gradient-to-br from-[#0F1419] to-[#1A1F2B] border border-[#61CE70]/30 rounded-3xl p-10 max-w-sm w-full text-center">
            <CheckCircle className="w-16 h-16 text-[#61CE70] mx-auto mb-5" />
            <h2 className="text-2xl font-bold text-white mb-3">Success!</h2>
            <p className="text-gray-300">Your transfer has been completed.</p>
          </div>
        </div>
      )}
    </div>
  );
}

export default TransferPage;
